import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/lib/auth';
import { useSessionTokenHash } from '@/hooks/useSessionTokenHash';
import {
  useUserSessions,
  useRevokeSession,
  useRevokeOtherSessions,
  useTrustSession,
} from '@/hooks/useUserSessions';
import { Banner } from '@/components/ui/Banner';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { PageLayout } from '@/components/ui/PageLayout';
import { Skeleton } from '@/components/ui/Skeleton';
import { StatusChip } from '@/components/ui/Chip';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';
import toast from 'react-hot-toast';
import {
  Monitor,
  Smartphone,
  Tablet,
  Globe,
  MapPin,
  Clock,
  CheckCircle2,
  XCircle,
  Shield,
} from 'lucide-react';
import type { UserSession } from '@/types/sessions';

function getDeviceIcon(deviceType: string | null) {
  switch (deviceType) {
    case 'mobile':
      return Smartphone;
    case 'tablet':
      return Tablet;
    case 'desktop':
      return Monitor;
    default:
      return Globe;
  }
}

function formatLastActive(timestamp: string): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 1000 / 60);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`;
  
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
}

function SessionCard({
  session,
  isCurrent,
  onRevoke,
  onTrust,
  revoking,
  trusting,
}: {
  session: UserSession;
  isCurrent: boolean;
  onRevoke: (id: string) => void;
  onTrust: (id: string) => void;
  revoking: boolean;
  trusting: boolean;
}) {
  const { t } = useTranslation();
  const DeviceIcon = getDeviceIcon(session.device_type);

  return (
    <Card>
      <div className="flex flex-col gap-s4 nav:flex-row nav:items-start nav:justify-between">
        <div className="flex min-w-0 gap-s3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-ds-teal-tint">
            <DeviceIcon className="h-5 w-5 text-ds-teal" aria-hidden />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-s2">
              <h3 className={cn(TYPE.section, 'text-ds-text dark:text-white')}>
                {session.device_name || 'Unknown Device'}
              </h3>
              {isCurrent && (
                <StatusChip tone="completed">{t('session.current', 'Current')}</StatusChip>
              )}
              {session.is_trusted && (
                <Shield className="h-4 w-4 text-ds-teal" aria-hidden />
              )}
            </div>
            
            <div className={cn(TYPE.small, 'mt-s1 space-y-1 text-ds-muted')}>
              {session.browser && session.os && (
                <div className="flex items-center gap-1.5">
                  <Globe className="h-3.5 w-3.5" />
                  <span>
                    {session.browser} · {session.os}
                  </span>
                </div>
              )}
              
              {session.ip_address && (
                <div className="flex items-center gap-1.5">
                  <MapPin className="h-3.5 w-3.5" />
                  <span>{session.ip_address}</span>
                </div>
              )}
              
              <div className="flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5" />
                <span>
                  {t('session.last_active', 'Last active')}: {formatLastActive(session.last_activity_at)}
                </span>
              </div>

              {session.is_trusted && session.trusted_until && (
                <div className="flex items-center gap-1.5 text-ds-teal">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  <span>
                    {t('session.trusted_until', 'Trusted until')}:{' '}
                    {new Date(session.trusted_until).toLocaleDateString()}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        {!isCurrent && (
          <div className="flex w-full flex-col gap-s2 nav:w-auto nav:flex-row">
            {!session.is_trusted && (
              <Button
                variant="secondary"
                size="sm"
                onClick={() => onTrust(session.id)}
                disabled={trusting}
              >
                {trusting ? t('session.trusting', 'Trusting...') : t('session.trust', 'Trust')}
              </Button>
            )}
            <Button
              variant="secondary"
              size="sm"
              onClick={() => onRevoke(session.id)}
              disabled={revoking}
            >
              {revoking ? t('session.revoking', 'Revoking...') : t('session.revoke', 'Revoke')}
            </Button>
          </div>
        )}
      </div>
    </Card>
  );
}

export function AccountSessions() {
  const { t } = useTranslation();
  const { user, session } = useAuth();
  const { data: sessions, isLoading, error } = useUserSessions(user?.id);
  const revokeSession = useRevokeSession();
  const revokeOtherSessions = useRevokeOtherSessions();
  const trustSession = useTrustSession();

  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [trustingId, setTrustingId] = useState<string | null>(null);
  const [revokingOthers, setRevokingOthers] = useState(false);

  const currentSessionToken = useSessionTokenHash(session?.access_token);

  const handleRevoke = async (sessionId: string) => {
    setRevokingId(sessionId);
    try {
      await revokeSession.mutateAsync({
        sessionId,
        reason: 'User manually revoked session',
      });
      toast.success(t('session.revoked', 'Session revoked successfully'));
    } catch (err) {
      console.error('Failed to revoke session:', err);
      toast.error(t('session.revoke_error', 'Failed to revoke session'));
    } finally {
      setRevokingId(null);
    }
  };

  const handleTrust = async (sessionId: string) => {
    setTrustingId(sessionId);
    try {
      await trustSession.mutateAsync({ sessionId, durationDays: 30 });
      toast.success(t('session.trusted', 'Device trusted for 30 days'));
    } catch (err) {
      console.error('Failed to trust session:', err);
      toast.error(t('session.trust_error', 'Failed to trust device'));
    } finally {
      setTrustingId(null);
    }
  };

  const handleRevokeOthers = async () => {
    if (!currentSessionToken) {
      toast.error(t('session.no_current', 'Current session not found'));
      return;
    }

    if (!window.confirm(t('session.revoke_others_confirm', 'Are you sure you want to log out all other devices?'))) {
      return;
    }

    setRevokingOthers(true);
    try {
      const count = await revokeOtherSessions.mutateAsync(currentSessionToken);
      toast.success(
        t('session.revoked_others', `Logged out ${count} other ${count === 1 ? 'session' : 'sessions'}`),
      );
    } catch (err) {
      console.error('Failed to revoke other sessions:', err);
      toast.error(t('session.revoke_others_error', 'Failed to log out other devices'));
    } finally {
      setRevokingOthers(false);
    }
  };

  if (isLoading) {
    return (
      <PageLayout
        title={t('session.title', 'Active Sessions')}
        subtitle={t('session.subtitle', 'Manage your active sessions and devices')}
      >
        <Card>
          <Skeleton className="h-16 w-full" />
        </Card>
      </PageLayout>
    );
  }

  if (error) {
    return (
      <PageLayout title={t('session.title', 'Active Sessions')}>
        <ErrorAlert message={error.message} />
      </PageLayout>
    );
  }

  const otherSessions = sessions?.filter((s) => s.session_token !== currentSessionToken) ?? [];

  return (
    <PageLayout
      title={t('session.title', 'Active Sessions')}
      subtitle={t('session.subtitle', 'Manage your active sessions and devices')}
      actions={
        otherSessions.length > 0 ? (
          <Button
            variant="secondary"
            className="w-full nav:w-auto"
            onClick={handleRevokeOthers}
            loading={revokingOthers}
            loadingText={t('session.revoking_others', 'Logging out...')}
          >
            <XCircle className="h-4 w-4" aria-hidden />
            {t('session.revoke_all_others', 'Log out all other devices')}
          </Button>
        ) : undefined
      }
      contentClassName="flex flex-col gap-s4"
    >

      <section className="flex flex-col gap-s4">
        {sessions?.length === 0 ? (
          <Card>
            <EmptyState
              icon={<Shield className="h-7 w-7" />}
              title={t('session.no_sessions', 'No active sessions found')}
            />
          </Card>
        ) : (
          sessions?.map((session) => (
            <SessionCard
              key={session.id}
              session={session}
              isCurrent={session.session_token === currentSessionToken}
              onRevoke={handleRevoke}
              onTrust={handleTrust}
              revoking={revokingId === session.id}
              trusting={trustingId === session.id}
            />
          ))
        )}
      </section>

      <Banner tone="info" title={t('session.security_tip', 'Security Tip')}>
        {t(
          'session.security_message',
          'If you see any suspicious sessions, revoke them immediately and change your password.',
        )}
      </Banner>
    </PageLayout>
  );
}
